
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useWarranty } from './hooks/useWarranty';
import { RepairOrder } from './types';

export default function RepairOrders() {
  const { repairs, claims, addRepair, updateRepair } = useWarranty();
  const { register, handleSubmit, reset } = useForm();
  const [showForm, setShowForm] = useState(false);
  const [selectedRepair, setSelectedRepair] = useState<RepairOrder | null>(null);

  const onSubmit = async (data: any) => {
    try {
      await addRepair.mutateAsync({
        ...data,
        id: `RO${Date.now()}`,
        status: 'in-progress',
        partsUsed: data.partsUsed ? data.partsUsed.split(',').map((p: string) => p.trim()) : [],
        cost: parseFloat(data.cost) || 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      reset();
      setShowForm(false);
    } catch (error) {
      console.error('Error adding repair order:', error);
    }
  };

  const updateRepairStatus = async (id: string, status: string, actualCompletion?: string) => {
    try {
      await updateRepair.mutateAsync({
        id,
        data: { 
          status, 
          actualCompletion,
          updatedAt: new Date().toISOString() 
        }
      });
    } catch (error) {
      console.error('Error updating repair:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'delayed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (estimatedCompletion: string) => {
    const today = new Date();
    const completion = new Date(estimatedCompletion);
    const daysLeft = Math.ceil((completion.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) return 'bg-red-100 text-red-800';
    if (daysLeft <= 2) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  if (repairs.isLoading) return <div className="p-6">Loading repair orders...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-[var(--color-primary)]">Repair Orders</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-[var(--color-primary)] text-white px-4 py-2 rounded-lg hover:bg-[var(--color-primary)]/90 transition-colors whitespace-nowrap cursor-pointer"
        >
          New Repair Order
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <h3 className="text-sm font-medium text-gray-600">In Progress</h3>
          <p className="text-2xl font-bold text-blue-600">
            {repairs.data?.filter((r: RepairOrder) => r.status === 'in-progress').length || 0}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <h3 className="text-sm font-medium text-gray-600">Completed</h3>
          <p className="text-2xl font-bold text-green-600">
            {repairs.data?.filter((r: RepairOrder) => r.status === 'completed').length || 0}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
          <h3 className="text-sm font-medium text-gray-600">Delayed</h3>
          <p className="text-2xl font-bold text-red-600">
            {repairs.data?.filter((r: RepairOrder) => r.status === 'delayed').length || 0}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-[var(--color-accent)]">
          <h3 className="text-sm font-medium text-gray-600">Total Cost</h3>
          <p className="text-2xl font-bold text-[var(--color-accent)]">
            ₹{repairs.data?.reduce((sum: number, r: RepairOrder) => sum + r.cost, 0).toLocaleString() || 0}
          </p>
        </div>
      </div>

      {/* Repair Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-[var(--color-accent)]/20">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Repair ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Claim ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Technician
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Issue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {repairs.data?.map((repair: RepairOrder) => (
                <tr key={repair.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {repair.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {repair.claimId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {repair.technician}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                    {repair.issue}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ₹{repair.cost.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(repair.estimatedCompletion)}`}>
                      {new Date(repair.estimatedCompletion).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(repair.status)}`}>
                      {repair.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    {repair.status === 'in-progress' && (
                      <>
                        <button
                          onClick={() => updateRepairStatus(repair.id, 'completed', new Date().toISOString())}
                          className="text-green-600 hover:text-green-900 cursor-pointer"
                        >
                          Complete
                        </button>
                        <button
                          onClick={() => updateRepairStatus(repair.id, 'delayed')}
                          className="text-red-600 hover:text-red-900 cursor-pointer"
                        >
                          Mark Delayed
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setSelectedRepair(repair)}
                      className="text-[var(--color-primary)] hover:text-[var(--color-primary)]/80 cursor-pointer"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Repair Order Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">New Repair Order</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <select
                {...register('claimId', { required: true })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent pr-8"
              >
                <option value="">Select Claim</option>
                {claims.data?.filter((c: any) => c.status === 'approved').map((claim: any) => (
                  <option key={claim.id} value={claim.id}>
                    {claim.id} - {claim.customer}
                  </option>
                ))}
              </select>
              <input
                {...register('technician', { required: true })}
                placeholder="Technician Name"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
              />
              <textarea
                {...register('issue', { required: true })}
                placeholder="Issue Description"
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
              />
              <input
                {...register('partsUsed')}
                placeholder="Parts Used (comma separated)"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
              />
              <input
                {...register('cost', { required: true })}
                type="number"
                step="0.01"
                placeholder="Repair Cost"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
              />
              <input
                {...register('estimatedCompletion', { required: true })}
                type="date"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
              />
              <textarea
                {...register('notes')}
                placeholder="Additional Notes"
                rows={2}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
              />
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={addRepair.isPending}
                  className="flex-1 bg-[var(--color-primary)] text-white py-2 px-4 rounded-lg hover:bg-[var(--color-primary)]/90 disabled:opacity-50 whitespace-nowrap cursor-pointer"
                >
                  {addRepair.isPending ? 'Creating...' : 'Create Order'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Repair Detail Modal */}
      {selectedRepair && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Repair Order Details</h2>
              <button
                onClick={() => setSelectedRepair(null)}
                className="text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Repair ID</label>
                <p className="mt-1 text-sm text-gray-900">{selectedRepair.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Claim ID</label>
                <p className="mt-1 text-sm text-gray-900">{selectedRepair.claimId}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Technician</label>
                <p className="mt-1 text-sm text-gray-900">{selectedRepair.technician}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Cost</label>
                <p className="mt-1 text-sm text-gray-900">₹{selectedRepair.cost.toLocaleString()}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Estimated Completion</label>
                <p className="mt-1 text-sm text-gray-900">{new Date(selectedRepair.estimatedCompletion).toLocaleDateString()}</p>
              </div>
              {selectedRepair.actualCompletion && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Actual Completion</label>
                  <p className="mt-1 text-sm text-gray-900">{new Date(selectedRepair.actualCompletion).toLocaleDateString()}</p>
                </div>
              )}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Issue Description</label>
                <p className="mt-1 text-sm text-gray-900">{selectedRepair.issue}</p>
              </div>
              {selectedRepair.partsUsed.length > 0 && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Parts Used</label>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {selectedRepair.partsUsed.map((part, index) => (
                      <span key={index} className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                        {part}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {selectedRepair.notes && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedRepair.notes}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <span className={`mt-1 inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedRepair.status)}`}>
                  {selectedRepair.status}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
